'use client' // Error boundaries must be Client Components

import { useCallback, startTransition } from 'react'

import { CWButton } from '@features/common.components/ComponentsWeb/Button'
import { Logo } from '@features/common.components/Header/Logo'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import useTranslation from 'next-translate/useTranslation'

export interface ErrorPageProps {
  reset: () => void
}

export const ErrorPage: React.FC<ErrorPageProps> = ({ reset }) => {
  const { t } = useTranslation('common')

  const router = useRouter()

  // `startTransition()` ensures that both client-side and server-side components are re-rendered together
  // full solution explanation: https://github.com/vercel/next.js/issues/63369#issuecomment-2211698627
  const handleRetry = useCallback(() => {
    startTransition(() => {
      reset()
      router.refresh()
    })
  }, [reset, router])

  return (
    <div
      className="relative flex h-screen w-screen items-center justify-center"
      data-testid="error-page"
    >
      <div className="absolute top-0 z-10 flex w-full items-center justify-center bg-white py-5">
        <Logo />
      </div>

      <div className="z-10 mx-6 flex w-full max-w-96 flex-col items-center rounded-md border border-neutral-subtle bg-white bg-opacity-75 py-6">
        <h1 className="mb-1.5 type-header-lg">{t`errorPage.title`}</h1>
        <p className="text-neutral-subtle type-body-lg">{t`errorPage.description1`}</p>
        <p className="text-neutral-subtle type-body-lg">{t`errorPage.description2`}</p>
        <CWButton
          className="mt-6"
          onClick={handleRetry}
          variant="primary"
        >{t`errorPage.buttonLabel`}</CWButton>
      </div>

      <Image
        alt={t`errorPage.backgroundImageAlt`}
        className="absolute left-0 top-0 size-full object-cover"
        src="/images/ErrorPageBackground.svg"
        fill
        priority
      />
    </div>
  )
}
